"use client";

import { getSlateSpacingPixelsString } from "@faire/retailer-shared/SlateMappings/getSlateSpacingPixels";
import { SDUIImageOrIconRenderer } from "@faire/retailer-visitor-shared/components/SDUI/ComponentRenderers/ImageOrIcon/SDUIImageOrIconRenderer";
import { ResponsiveContentV2 } from "@faire/web--source/ui/ResponsiveContentV2";
import { Alignment } from "@faire/web-api--source/indigofair/data/Alignment";
import { IBackgroundIcon } from "@faire/web-api--source/indigofair/data/IBackgroundIcon";
import { IOffsetSpacing } from "@faire/web-api--source/indigofair/data/IOffsetSpacing";
import { IResponsiveBackgroundIcon } from "@faire/web-api--source/indigofair/data/IResponsiveBackgroundIcon";
import { Spacing } from "@faire/web-api--source/indigofair/data/Spacing";
import React from "react";

export const SDUIBackgroundIcon: React.FC<{
  backgroundIcon: IBackgroundIcon | undefined;
  responsiveBackgroundIcon: IResponsiveBackgroundIcon | undefined;
}> = ({ backgroundIcon, responsiveBackgroundIcon }) => {
  const mobileBackgroundIcon =
    responsiveBackgroundIcon?.mobile_and_above ?? backgroundIcon;
  const tabletBackgroundIcon =
    responsiveBackgroundIcon?.tablet_and_above ?? mobileBackgroundIcon;
  const desktopAndAboveBackgroundIcon =
    responsiveBackgroundIcon?.desktop_and_above ?? tabletBackgroundIcon;

  return (
    <ResponsiveContentV2
      mobile={
        mobileBackgroundIcon?.icon ? (
          <SDUIImageOrIconRenderer
            dataTestId="sdui-background-icon-mobile"
            imageOrIcon={mobileBackgroundIcon.icon}
            componentStyle={getInlineStylesForBackgroundIcon(
              mobileBackgroundIcon
            )}
          />
        ) : undefined
      }
      tablet={
        tabletBackgroundIcon?.icon ? (
          <SDUIImageOrIconRenderer
            dataTestId="sdui-background-icon-tablet"
            imageOrIcon={tabletBackgroundIcon.icon}
            componentStyle={getInlineStylesForBackgroundIcon(
              tabletBackgroundIcon
            )}
          />
        ) : undefined
      }
      desktopAndAbove={
        desktopAndAboveBackgroundIcon?.icon ? (
          <SDUIImageOrIconRenderer
            dataTestId="sdui-background-icon-desktop"
            imageOrIcon={desktopAndAboveBackgroundIcon.icon}
            componentStyle={getInlineStylesForBackgroundIcon(
              desktopAndAboveBackgroundIcon
            )}
          />
        ) : undefined
      }
      noJS
    />
  );
};

/**
 * Assigns CSS styles for correct placement of the absolutely positioned background icon
 * based on server provided `position` values.
 */
const getInlineStylesForBackgroundIcon = (
  backgroundIcon: IBackgroundIcon
): React.CSSProperties => {
  const inlineStyleVars = getInlineStyleVars(backgroundIcon);

  return {
    top: inlineStyleVars?.top,
    bottom: inlineStyleVars?.bottom,
    left: inlineStyleVars?.left,
    right: inlineStyleVars?.right,
    transform: inlineStyleVars?.transform,
    position: "absolute",
  };
};

type InlineStyleVars = {
  top: string | undefined;
  left: string | undefined;
  right: string | undefined;
  bottom: string | undefined;
  transform: string | undefined;
};

const getInlineStyleVars = (
  backgroundIcon: IBackgroundIcon | undefined
): InlineStyleVars | undefined => {
  if (!backgroundIcon?.position) {
    return undefined;
  }

  const topValue = getTopLeftStyleValue(
    backgroundIcon.position.vertical_alignment
  );
  const leftValue = getTopLeftStyleValue(
    backgroundIcon.position.horizontal_alignment
  );
  const rightValue = getBottomRightStyleValue(
    backgroundIcon.position.horizontal_alignment
  );
  const bottomValue = getBottomRightStyleValue(
    backgroundIcon.position.vertical_alignment
  );
  const transformValue = getTransformStyleVar(
    leftValue,
    backgroundIcon.position.horizontal_offset,
    topValue,
    backgroundIcon.position.vertical_offset
  );

  return {
    top: topValue,
    left: leftValue,
    right: rightValue,
    bottom: bottomValue,
    transform: transformValue,
  };
};

const getTopLeftStyleValue = (
  alignment: keyof typeof Alignment | undefined
) => {
  switch (alignment) {
    case Alignment.ALIGNMENT_START:
      return "0px";
    case Alignment.ALIGNMENT_CENTER:
      return "50%";
  }
  return undefined;
};

const getBottomRightStyleValue = (
  alignment: keyof typeof Alignment | undefined
) => {
  return alignment === Alignment.ALIGNMENT_END ? "0" : undefined;
};

/**
 * Negative values are used to center the position:absolute icon if ALIGNMENT_CENTER
 * but we also need to combine it with the offset to get the final transform value.
 * We use 0px instead of 0 because calc does not work with unitless values
 */
const getTransformStyleVar = (
  initialTransformValueX: string | undefined,
  offsetTransformValueX: IOffsetSpacing | undefined,
  initialTransformValueY: string | undefined,
  offsetTransformValueY: IOffsetSpacing | undefined
) =>
  `translate(calc(-${
    initialTransformValueX ?? "0px"
  } + ${getOffsetTransformValue(offsetTransformValueX)}), calc(-${
    initialTransformValueY ?? "0px"
  } + ${getOffsetTransformValue(offsetTransformValueY)}))`;

/**
 * Calculates the px offset of the icon in pixels based on the current viewport size.
 * Falls back to no offset if not defined.
 */
const getOffsetTransformValue = (offset: IOffsetSpacing | undefined): string =>
  `${offset?.positive ? "" : "-"}${getSlateSpacingPixelsString(
    offset?.positive ?? offset?.negative ?? Spacing.SPACING_NONE
  )}`;
